import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UrlTabItem {
  id: string;
  label: string;
  /** Small pill after the label. A number renders a count, `true` renders a bare dot. */
  badge?: number | true;
}

export interface UrlTabsProps {
  param: string;
  tabs: readonly UrlTabItem[];
  defaultId: string;
  testIdPrefix: string;
  className?: string;
}

/**
 * Reads/writes a value that lives in the query string. An unknown or
 * missing value resolves to `defaultId`. Selecting the default deletes the
 * param instead of writing it, so the URL stays tidy and the browser back
 * button doesn't bounce between equivalent states.
 */
export function useUrlTab(
  param: string,
  ids: readonly string[],
  defaultId: string,
): [string, (id: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);
  const active = raw != null && ids.includes(raw) ? raw : defaultId;

  const setActive = (next: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === defaultId) nextParams.delete(param);
    else nextParams.set(param, next);
    setSearchParams(nextParams, { replace: true });
  };

  return [active, setActive];
}

/**
 * Horizontal tab row whose active tab lives in `?<param>=`. Reuses the
 * `border-b-2` / `primary-500` visual language every tab list in the app
 * already used before UX-01.
 */
export const UrlTabs: React.FC<UrlTabsProps> = ({
  param,
  tabs,
  defaultId,
  testIdPrefix,
  className = '',
}) => {
  const ids = useMemo(() => tabs.map((t) => t.id), [tabs]);
  const [active, setActive] = useUrlTab(param, ids, defaultId);

  return (
    <nav role="tablist" className={'border-b border-slate-200 flex gap-1 -mt-2 ' + className}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          data-testid={`${testIdPrefix}-${t.id}`}
          onClick={() => setActive(t.id)}
          className={
            'px-3 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-150 inline-flex items-center gap-1.5 ' +
            (active === t.id
              ? 'border-primary-500 text-primary-600 font-semibold'
              : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-200')
          }
        >
          <span>{t.label}</span>
          {typeof t.badge === 'number' && (
            <span
              data-testid={`${testIdPrefix}-${t.id}-badge`}
              className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-slate-200 text-slate-700 text-2xs font-semibold leading-none"
            >
              {t.badge}
            </span>
          )}
          {t.badge === true && (
            <span
              data-testid={`${testIdPrefix}-${t.id}-badge`}
              aria-hidden
              className="w-1.5 h-1.5 rounded-full bg-primary-500"
            />
          )}
        </button>
      ))}
    </nav>
  );
};

export default UrlTabs;
